/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  CopyButton,
  RenderAmount,
  useBankCoreApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { Transactions } from "../../components/Transactions/index.js";
import { PaymentOptions } from "../PaymentOptions.js";
import { State } from "./index.js";

function AccountAddressMenu({
  accountPaytoUri,
  accountLegalName,
  accountLoginName,
  accountBankHost,
}: {
  accountPaytoUri: string;
  accountLegalName: string;
  accountLoginName: string;
  accountBankHost?: string;
}): VNode {
  const { i18n } = useTranslationContext();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const shortAddress = accountBankHost
    ? `${accountLoginName} @ ${accountBankHost}`
    : accountLoginName;

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: PointerEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div class="relative" ref={containerRef}>
      <p class="text-sm font-semibold text-secondary">
        <i18n.Translate>Account address</i18n.Translate>
      </p>
      <p
        class="mt-2 truncate text-sm font-medium text-onBackground"
        title={accountLegalName}
      >
        {accountLegalName}
      </p>
      <button
        ref={triggerRef}
        type="button"
        class="mt-0.5 inline-flex max-w-full items-center gap-1 text-left text-sm text-secondary hover:underline hover:underline-offset-2 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        aria-expanded={open}
        aria-controls="account-address-actions"
        onClick={() => setOpen((value) => !value)}
        title={shortAddress}
      >
        <span class="truncate">{shortAddress}</span>
        <svg
          class={`h-3.5 w-3.5 shrink-0 text-secondary transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="m6 9 6 6 6-6"
          />
        </svg>
      </button>
      {open ? (
        <div
          id="account-address-actions"
          class="absolute left-0 z-20 mt-1 min-w-56 rounded-lg border border-outlineVariant bg-white p-1 shadow-lg"
        >
          <CopyButton
            class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-onBackground hover:bg-primary/10 hover:text-primaryDark focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            getContent={() => accountPaytoUri}
            onCopyComplete={() => setOpen(false)}
          >
            <i18n.Translate>Copy account address</i18n.Translate>
          </CopyButton>
        </div>
      ) : undefined}
    </div>
  );
}

export function ReadyView({
  account,
  accountPaytoUri,
  accountLegalName,
  accountLoginName,
  accountBankHost,
  routeChargeWallet,
  routeWireTransfer,
  balance,
  balanceIsDebit,
  routeCashout,
  routeOperationDetails,
  routeTransactions,
  routeAccountDetails,
  showRecentActivity = true,
}: State.Ready): VNode {
  const { config } = useBankCoreApiContext();
  const { i18n } = useTranslationContext();
  return (
    <Fragment>
      <section
        class="rounded-xl border border-secondary/20 bg-secondaryContainer px-5 py-5 sm:px-6"
        aria-labelledby="current-balance-title"
      >
        <div class="grid gap-5 sm:grid-cols-[minmax(12rem,0.8fr)_minmax(0,1.2fr)] sm:items-center">
          <div>
            <p
              id="current-balance-title"
              class="text-sm font-semibold text-secondary"
            >
              <i18n.Translate>Current balance</i18n.Translate>
            </p>
            <div class="mt-1 text-3xl font-bold tracking-tight text-brand sm:text-4xl">
              <RenderAmount
                value={balance}
                negative={balanceIsDebit}
                withSign
                spec={config.currency_specification}
              />
            </div>
            <a
              class="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primaryDark hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              href={routeAccountDetails.url({})}
            >
              <i18n.Translate>Go to account details</i18n.Translate>
              <svg
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m9 18 6-6-6-6"
                />
              </svg>
            </a>
          </div>
          <div class="min-w-0 border-t border-primary/20 pt-4 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0">
            <AccountAddressMenu
              accountPaytoUri={accountPaytoUri}
              accountLegalName={accountLegalName}
              accountLoginName={accountLoginName}
              accountBankHost={accountBankHost}
            />
          </div>
        </div>
      </section>
      <PaymentOptions
        routeOperationDetails={routeOperationDetails}
        routeCashout={routeCashout}
        routeChargeWallet={routeChargeWallet}
        routeWireTransfer={routeWireTransfer}
      />
      {showRecentActivity ? (
        <Transactions
          account={account}
          variant="recent"
          routeFullHistory={routeTransactions}
        />
      ) : undefined}
    </Fragment>
  );
}
